import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  CalendarDays, Clock, ChevronLeft, ChevronRight, Loader2,
  Sparkles, ShieldCheck, ArrowLeft, CheckCircle2,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useT } from "../i18n/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtPrice = (cents) => `$${(cents / 100).toLocaleString("en-US")}`;

function toKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const DOW_LABELS = {
  en: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
  ht: ["Di", "Li", "Ma", "Mè", "Je", "Va", "Sa"],
};

export function MonthCalendar({ selected, onSelect }) {
  const { lang } = useT();
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const today = toKey(new Date());
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  const locale = lang === "ht" ? "ht-HT" : "en-US";

  return (
    <div data-testid="booking-calendar" className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-5">
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          data-testid="cal-prev-month"
          onClick={() => setCursor(new Date(year, month - 1, 1))}
          className="p-2 rounded-full hover:bg-[#F8F7F3] text-[#3F5043]"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="font-serif italic text-lg text-[#3F5043]">
          {cursor.toLocaleString(locale, { month: "long", year: "numeric" })}
        </div>
        <button
          type="button"
          data-testid="cal-next-month"
          onClick={() => setCursor(new Date(year, month + 1, 1))}
          className="p-2 rounded-full hover:bg-[#F8F7F3] text-[#3F5043]"
        >
          <ChevronRight size={18} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] uppercase tracking-wider text-[#899480] mb-2">
        {(DOW_LABELS[lang] || DOW_LABELS.en).map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={`x${i}`} />;
          const key = toKey(d);
          const past = key < today;
          const active = key === selected;
          return (
            <button
              key={key}
              type="button"
              data-testid={`cal-day-${key}`}
              disabled={past}
              onClick={() => onSelect(key)}
              className={`aspect-square rounded-xl text-sm transition-colors ${
                active
                  ? "bg-[#3F5043] text-[#F8F7F3] font-semibold"
                  : past
                    ? "text-[#BCB3A6]/50 cursor-not-allowed"
                    : "text-[#3F5043] hover:bg-[#899480]/20"
              }`}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Book() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, lang } = useT();
  const [services, setServices] = useState([]);
  const [service, setService] = useState(null);
  const [team, setTeam] = useState([]);
  const [staff, setStaff] = useState("");  // "" = no preference
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", notes: "" });
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/booking/services`).then((r) => {
      setServices(r.data);
      const pre = searchParams.get("service");
      if (pre) {
        const found = r.data.find((s) => s.key === pre);
        if (found) setService(found);
      }
    }).catch(() => {});
    axios.get(`${API}/booking/team`).then((r) => setTeam(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!date) return;
    setSlotsLoading(true);
    setTime("");
    axios
      .get(`${API}/booking/availability`, { params: { date, staff } })
      .then((r) => setSlots(r.data.slots))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [date, staff]);

  const ready = service && date && time && form.full_name.trim() && form.email.trim() && agreed;
  const isRequestOnly = service?.key === "interpreter";
  const [requestSent, setRequestSent] = useState(false);
  const [paypalOn, setPaypalOn] = useState(false);

  useEffect(() => {
    axios.get(`${API}/paypal/status`).then((r) => setPaypalOn(!!r.data.configured)).catch(() => {});
  }, []);

  const submitPaypal = async () => {
    if (!ready || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await axios.post(`${API}/booking/paypal`, {
        service: service.key,
        date,
        time,
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        notes: form.notes,
        origin_url: window.location.origin,
        staff_email: staff,
      });
      window.location.href = res.data.approve_url;
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not start PayPal — you can pay by card instead.");
      setSubmitting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!ready || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await axios.post(`${API}/booking/checkout`, {
        service: service.key,
        date,
        time,
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        notes: form.notes,
        origin_url: window.location.origin,
        staff_email: staff,
      });
      if (res.data.pending_review) {
        setRequestSent(true);
        setSubmitting(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      window.location.href = res.data.checkout_url;
    } catch (err) {
      setError(err?.response?.data?.detail || t("book.errorGeneric"));
      setSubmitting(false);
    }
  };

  const steps = useMemo(
    () => [
      { n: 1, label: t("book.step1"), done: !!service },
      { n: 2, label: t("book.step2"), done: !!date && !!time },
      { n: 3, label: t("book.step3"), done: false },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [service, date, time, lang]
  );

  const inputCls =
    "w-full bg-white border border-[#BCB3A6]/50 rounded-2xl px-4 py-3 text-sm text-[#3F5043] placeholder-[#3F5043]/40 focus:outline-none focus:ring-2 focus:ring-[#899480]";

  return (
    <div className="min-h-screen bg-[#F8F7F3]" data-testid="book-page">
      <Header />
      <main className="max-w-4xl mx-auto px-5 py-12 sm:py-16">
        <div className="text-center mb-10">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#AE8F78] font-semibold mb-3">
            {t("book.eyebrow")}
          </div>
          <h1 className="font-serif italic text-4xl sm:text-5xl text-[#3F5043] mb-3">
            {t("book.title")}
          </h1>
          <p className="text-sm sm:text-base text-[#3F5043]/70 max-w-xl mx-auto leading-relaxed">
            {t("book.subtitle")}
          </p>
        </div>

        <div className="flex items-center justify-center gap-2 mb-10">
          {steps.map((s, i) => (
            <React.Fragment key={s.n}>
              {i > 0 && <div className="w-8 h-px bg-[#BCB3A6]/60" />}
              <div className="flex items-center gap-2">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                    s.done ? "bg-[#899480] text-white" : "bg-[#3F5043]/10 text-[#3F5043]"
                  }`}
                >
                  {s.done ? <CheckCircle2 size={14} /> : s.n}
                </div>
                <span className="text-xs text-[#3F5043]/70">{s.label}</span>
              </div>
            </React.Fragment>
          ))}
        </div>

        {requestSent && (
          <div data-testid="booking-request-sent" className="max-w-xl mx-auto bg-white rounded-3xl border border-[#899480]/40 p-10 text-center mb-10">
            <div className="w-14 h-14 rounded-full bg-[#899480]/15 text-[#3F5043] flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={26} strokeWidth={1.5} />
            </div>
            <h2 className="font-serif italic text-3xl text-[#3F5043] mb-3">Request received</h2>
            <p className="text-sm text-[#3F5043]/70 leading-relaxed mb-2">
              Thank you, {form.full_name.split(" ")[0] || "friend"}. We've received your Attend With Me
              request for <strong>{date} at {time} ET</strong>.
            </p>
            <p className="text-sm text-[#3F5043]/70 leading-relaxed">
              Our team will confirm availability first — once accepted, we'll text and email you a secure
              link to complete your payment. No charge has been made.
            </p>
          </div>
        )}

        {/* STEP 1 — service */}
        <section className="mb-10">
          <h2 className="font-serif text-2xl text-[#3F5043] mb-4">{t("book.chooseService")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {services.map((s) => (
              <button
                key={s.key}
                type="button"
                data-testid={`service-${s.key}`}
                onClick={() => setService(s)}
                className={`text-left bg-white rounded-3xl p-5 border-2 transition-all ${
                  service?.key === s.key
                    ? "border-[#3F5043] shadow-lg"
                    : "border-[#BCB3A6]/40 hover:border-[#899480]"
                }`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="font-serif text-lg text-[#3F5043]">{s.name}</div>
                  <div className="font-serif text-xl text-[#875F45] whitespace-nowrap">
                    {fmtPrice(s.amount)}
                  </div>
                </div>
                <p className="text-xs text-[#3F5043]/70 leading-relaxed mb-3">{s.description}</p>
                <div className="flex items-center gap-3 text-[11px] text-[#899480]">
                  <span className="inline-flex items-center gap-1">
                    <Clock size={12} /> {s.duration} {t("book.min")}
                  </span>
                  {s.grants_portal && (
                    <span className="inline-flex items-center gap-1 text-[#AE8F78] font-semibold">
                      <Sparkles size={12} /> {t("book.includesPortal")}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* Gentle upsell: First Consultation → support packages */}
        {service?.key === "first-consult" && (
          <div
            data-testid="book-upsell-hint"
            className="mb-10 bg-[#3F5043] rounded-3xl p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <Sparkles size={22} className="text-[#AE8F78] shrink-0" />
            <p className="text-sm text-[#F8F7F3]/85 leading-relaxed flex-1">
              {t("book.upsellText")}
            </p>
            <div className="flex gap-2 shrink-0">
              <button
                type="button"
                data-testid="book-upsell-iui"
                onClick={() => setService(services.find((s) => s.key === "iui-support"))}
                className="rounded-full px-4 py-2 bg-[#F8F7F3]/10 text-[#F8F7F3] text-xs font-medium hover:bg-[#AE8F78] transition-colors"
              >
                IUI Support · $800
              </button>
              <button
                type="button"
                data-testid="book-upsell-ivf"
                onClick={() => setService(services.find((s) => s.key === "ivf-support"))}
                className="rounded-full px-4 py-2 bg-[#AE8F78] text-[#3F5043] text-xs font-semibold hover:bg-[#F8F7F3] transition-colors"
              >
                IVF Support · $1,250
              </button>
            </div>
          </div>
        )}

        {/* STEP 2 — date & time */}
        {service && (
          <section className="mb-10">
            <h2 className="font-serif text-2xl text-[#3F5043] mb-4">{t("book.pickTime")}</h2>
            {team.length > 0 && (
              <div className="mb-4" data-testid="team-picker">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#899480] font-semibold mb-2">
                  {t("book.withWhom")}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => setStaff("")} data-testid="team-any"
                    className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                      staff === "" ? "bg-[#3F5043] text-[#F8F7F3]" : "bg-white border border-[#BCB3A6]/50 text-[#3F5043] hover:bg-[#899480]/10"}`}>
                    {t("book.noPreference")}
                  </button>
                  {team.map((m) => (
                    <button type="button" key={m.email} onClick={() => setStaff(m.email)}
                      data-testid={`team-${m.email}`}
                      className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                        staff === m.email ? "bg-[#3F5043] text-[#F8F7F3]" : "bg-white border border-[#BCB3A6]/50 text-[#3F5043] hover:bg-[#899480]/10"}`}>
                      {m.name} · {m.role_label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <MonthCalendar selected={date} onSelect={setDate} />
              <div className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-5">
                <div className="flex items-center gap-2 text-sm font-medium text-[#3F5043] mb-4">
                  <CalendarDays size={16} className="text-[#AE8F78]" />
                  {date
                    ? new Date(`${date}T12:00:00`).toLocaleDateString(lang === "ht" ? "ht-HT" : "en-US", {
                        weekday: "long", month: "long", day: "numeric",
                      })
                    : t("book.selectDate")}
                </div>
                {slotsLoading && (
                  <div className="flex items-center gap-2 text-sm text-[#3F5043]/60">
                    <Loader2 size={15} className="animate-spin" /> {t("book.loadingTimes")}
                  </div>
                )}
                {!slotsLoading && slots && slots.length === 0 && (
                  <p className="text-sm text-[#3F5043]/60" data-testid="no-slots-message">
                    {t("book.noSlots")}
                  </p>
                )}
                {!slotsLoading && slots && slots.length > 0 && (
                  <div className="grid grid-cols-3 gap-2" data-testid="time-slots">
                    {slots.map((t) => (
                      <button
                        key={t}
                        type="button"
                        data-testid={`slot-${t}`}
                        onClick={() => setTime(t)}
                        className={`rounded-xl px-2 py-2.5 text-sm transition-colors ${
                          time === t
                            ? "bg-[#3F5043] text-[#F8F7F3] font-semibold"
                            : "bg-[#F8F7F3] text-[#3F5043] hover:bg-[#899480]/20"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* STEP 3 — details */}
        {service && date && time && (
          <section className="mb-10">
            <h2 className="font-serif text-2xl text-[#3F5043] mb-4">{t("book.details")}</h2>
            <form
              onSubmit={submit}
              data-testid="booking-form"
              className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-6 space-y-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input
                  data-testid="booking-name"
                  required
                  placeholder={t("book.name")}
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  className={inputCls}
                />
                <input
                  data-testid="booking-email"
                  required
                  type="email"
                  placeholder={t("book.email")}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={inputCls}
                />
                <input
                  data-testid="booking-phone"
                  type="tel"
                  placeholder={t("book.phone")}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className={inputCls}
                />
                <textarea
                  data-testid="booking-notes"
                  placeholder={t("book.notes")}
                  rows={1}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className={`${inputCls} resize-none`}
                />
              </div>

              <div className="bg-[#875F45]/5 border border-[#875F45]/20 rounded-2xl p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    data-testid="booking-policy-checkbox"
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-1 accent-[#3F5043]"
                  />
                  <span className="text-xs text-[#3F5043]/80 leading-relaxed">
                    <strong className="text-[#875F45]">{t("book.policyStrong")}</strong>{" "}
                    {t("book.policy")}
                  </span>
                </label>
              </div>

              {error && (
                <p className="text-sm text-red-700" data-testid="booking-error">{error}</p>
              )}

              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
                <div className="text-sm text-[#3F5043]/70">
                  <span className="font-serif text-xl text-[#3F5043]">{service.name}</span>
                  <span className="mx-2">·</span>
                  {date} at {time} ET
                  <span className="mx-2">·</span>
                  <span className="font-semibold text-[#875F45]">{fmtPrice(service.amount)}</span>
                </div>
                <div className="flex flex-col items-stretch gap-2">
                  <button
                    type="submit"
                    data-testid="booking-pay-button"
                    disabled={!ready || submitting}
                    className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-3.5 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium hover:bg-[#875F45] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {submitting ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />}
                    {isRequestOnly ? "Send request — pay after we confirm" : `${fmtPrice(service.amount)} · ${t("book.pay")}`}
                  </button>
                  {paypalOn && !isRequestOnly && (
                    <button
                      type="button"
                      onClick={submitPaypal}
                      data-testid="booking-paypal-button"
                      disabled={!ready || submitting}
                      className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-3 border-2 border-[#D4AF37] text-[#875F45] text-sm font-semibold hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {submitting ? <Loader2 size={15} className="animate-spin" /> : null}
                      Pay with PayPal
                    </button>
                  )}
                </div>
              </div>
            </form>
          </section>
        )}

        <div className="text-center">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 text-xs text-[#3F5043]/60 hover:text-[#3F5043] transition-colors"
          >
            <ArrowLeft size={13} /> {t("book.back")}
          </button>
        </div>
      </main>
      <Footer />
    </div>
  );
}
